import { Button, IconButton, Stack, Tooltip } from '@chronosphereio/chrono-ui';
import { InputBase, type InputBaseProps } from '@mui/material';
import { Close as CloseIcon, KeyboardReturn as KeyboardReturnIcon } from 'mdi-material-ui';
import { useEffect, useRef } from 'react';
import { useQueryEditorContext } from '../../query-editor-context';
import { AIEditShortcutPill } from './AIEditShortcutPill';
import { AIEditThinkingState } from './AIEditThinkingState';
import { useAIEdit } from './ai-edit-context';

export interface AIEditPanelProps {
  /**
   * Messages cycled during the loading/thinking state.
   * Language-specific consumers provide messages relevant to their domain
   * (e.g., "Generating PromQL..." vs "Analyzing logs...").
   */
  thinkingMessages?: string[];
}

/**
 * Prompt input panel for the AI Edit action. Renders inside a
 * QueryEditor.Container, below the QueryEditor.InputContainer.
 *
 * Shows previous prompts, an active text input with keyboard shortcuts,
 * a generate button, and a loading/thinking indicator.
 */
export function AIEditPanel({ thinkingMessages }: AIEditPanelProps) {
  const { isOpen, hasInputValue, isLoading, previousPrompts, submit, close } = useAIEdit();

  if (!isOpen) return null;

  const generateButton = hasInputValue && !isLoading && (
    <Button size="small" variant="tertiary" onClick={() => submit()} sx={{ px: 1, py: 0.25, whiteSpace: 'nowrap' }}>
      Generate
      <AIEditShortcutPill sx={{ ml: 0.5 }}>
        <KeyboardReturnIcon />
      </AIEditShortcutPill>
    </Button>
  );

  return (
    <Stack
      gap={0.5}
      paddingX={1.5}
      paddingY={1}
      maxHeight={200}
      overflow="auto"
      borderRadius={1}
      bgcolor="background.paper"
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Stack flexGrow={1}>
          {previousPrompts.length === 0 ? <ActivePromptInput /> : <DisabledPromptInput value={previousPrompts[0]!} />}
        </Stack>
        {previousPrompts.length === 0 && generateButton}
        <Tooltip Content="Close">
          <IconButton aria-label="Close" size="small" onClick={close}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {previousPrompts.length > 0 && (
        <>
          {previousPrompts.slice(1).map((prompt, index) => (
            <DisabledPromptInput key={index} value={prompt} />
          ))}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <ActivePromptInput />
            {generateButton}
          </Stack>
        </>
      )}

      {isLoading && <AIEditThinkingState messages={thinkingMessages} maxDuration={4} />}
    </Stack>
  );
}

function ActivePromptInput() {
  const { value, onRun } = useQueryEditorContext();
  const { inputValue, setInputValue, isLoading, isOpen, suggestion, submit, accept, acceptAndRun, reject, close } =
    useAIEdit();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isLoading && isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading, isOpen]);

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      if (suggestion && onRun) {
        e.preventDefault();
        e.stopPropagation();
        acceptAndRun();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      await submit();
    } else if (e.key === 'Tab') {
      if (suggestion) {
        e.preventDefault();
        e.stopPropagation();
        accept();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (suggestion) {
        reject();
      } else {
        close();
      }
    } else if (e.metaKey && !e.shiftKey && e.key === 'k') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };

  return (
    <PromptInput
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      inputRef={inputRef}
      inputProps={{ readOnly: isLoading }}
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={value ? 'Refine your query' : 'Describe your query'}
    />
  );
}

function DisabledPromptInput({ value }: { value: string }) {
  return <PromptInput value={value} disabled sx={{ color: 'text.secondary' }} />;
}

function PromptInput(props: InputBaseProps) {
  return (
    <InputBase
      {...props}
      fullWidth
      sx={{
        backgroundColor: 'inherit',
        '&.MuiInputBase-root': { py: '2px !important' },
        ...props.sx,
      }}
    />
  );
}
